import { useState } from "react";
import { toast } from "sonner";
import { Megaphone, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { GoldButton, GhostButton, Field } from "@/components/atlas/Fields";
import { api } from "@/lib/api";
import { useLang, errorText } from "@/lib/i18n";

export const AnnounceDialog = ({ event, open, onClose }) => {
  const { t } = useLang();
  const [audience, setAudience] = useState("qualified");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/events/${event.id}/announce`, { audience, message });
      toast.success(`${t("an.sent")} (${r.data.sent})`);
      setMessage("");
      onClose();
    } catch (e) {
      toast.error(errorText(t, e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="rounded-2xl" data-testid="announce-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#C5A059]" />{t("an.title")}</DialogTitle>
          <DialogDescription>{event.name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <Field label={t("an.audience")}>
            <div className="grid sm:grid-cols-2 gap-2">
              {["qualified", "assigned"].map((k) => (
                <button key={k} type="button" data-testid={`announce-audience-${k}`} aria-pressed={audience === k} onClick={() => setAudience(k)}
                  className={`press text-left border rounded-xl px-4 py-3 ${audience === k ? "border-[#C5A059] bg-[#C5A059]/10" : "border-black/10 hover:border-black/30"}`}>
                  <div className="text-sm font-medium">{t(`an.${k}`)}</div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">{t(`an.${k}Hint`)}</div>
                </button>
              ))}
            </div>
          </Field>
          <Field label={t("an.message")}>
            <Textarea data-testid="announce-message" value={message} maxLength={1000} onChange={(e) => setMessage(e.target.value)} placeholder={t("an.placeholder")} className="min-h-[110px] rounded-none" />
          </Field>
        </div>
        <DialogFooter className="gap-2">
          <GhostButton onClick={onClose} disabled={busy}>{t("c.cancel")}</GhostButton>
          <GoldButton data-testid="announce-send" onClick={send} disabled={busy}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}{t("an.send")}</GoldButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
